// §sign-out — a discoverable, cross-tab-aware sign-out for the V2 shell.
//
// P0 background: the shipped V2 UI's ONLY reachable sign-out lived at the
// bottom of the Account screen's red "Danger zone" (next to permanent account
// deletion). Users couldn't find it and were destroying browser profiles to
// switch accounts. The fix surfaces a clear Sign out control in the shell's
// account area (desktop rail + mobile drawer); this module holds the shared
// behaviour behind it so every entry point acts identically.
//
// The heavy lifting is already done elsewhere and is NOT re-implemented here:
//   • POST /v1/auth/sign-out (workers/api-gateway) clears the httpOnly session
//     cookie AND records a per-account revocation epoch (a token captured
//     before logout is rejected — see tests/logout-invalidation.test.mjs).
//   • useAuth().signOut() calls that endpoint with credentials and, on 200,
//     resets the shared in-memory session cache so every useAuth() consumer
//     re-renders as signed-out (rail user → "Sign in", Account → signed-out
//     panel). A reload re-derives auth from the now-cleared cookie, so it
//     stays signed out.
//   • CreatorRoot rebinds the local recents bucket to 'anon' when
//     session.signedIn flips false (setWorkspaceOwner), which clears the
//     signed-in person's local workspace cache for privacy WITHOUT deleting
//     their account-scoped cloud apps. Sign-out ≠ delete.
//
// This module adds the one thing the above can't do on its own: telling the
// user's OTHER open Pomela tabs to drop to signed-out too.

/** localStorage key whose write is observed by sibling tabs' `storage`
 *  listeners. A monotonically fresh value guarantees an event fires even on
 *  a second sign-out. This holds NO credential — only a timestamp. */
export const SIGNOUT_BROADCAST_KEY = 'pomela:auth:signed-out';

/** Write a fresh value to the broadcast key so OTHER tabs receive a `storage`
 *  event. (The writing tab never receives its own storage event — that is the
 *  DOM contract, which is exactly why cross-tab sync needs an explicit hop.) */
export function broadcastSignOut(): void {
  try {
    localStorage.setItem(SIGNOUT_BROADCAST_KEY, String(Date.now()));
  } catch {
    /* storage blocked (Safari private / quota) — the server cookie clear +
       in-memory reset still sign THIS tab out; only cross-tab sync is lost. */
  }
}

/**
 * Run the shared sign-out sequence for the CURRENT tab.
 *
 *  - `hookSignOut` is `useAuth().signOut` — it POSTs /v1/auth/sign-out with
 *    credentials and, on 200, resets the shared session cache.
 *  - On success we broadcast to sibling tabs and return `true` so the caller
 *    can route to a signed-out surface.
 *  - On failure we return `false` and DO NOT broadcast and DO NOT route — the
 *    caller must show an honest error and keep the control live. No fake
 *    success: a failed network call must never look like a sign-out.
 *
 * We deliberately do NOT wipe the user's apps/workspace/projects here:
 * sign-out preserves all user-owned data (only delete erases it). Local
 * recents are rebound to 'anon' by CreatorRoot's identity effect, which is
 * privacy isolation, not deletion — the cloud copy is untouched and rehydrates
 * on the next sign-in.
 */
export async function performSignOut(hookSignOut: () => Promise<boolean>): Promise<boolean> {
  const ok = await hookSignOut();
  if (ok) broadcastSignOut();
  return ok;
}

/**
 * Subscribe THIS tab to sibling-tab sign-outs. Returns an unsubscribe fn.
 *
 * When another tab signs out, `onSignedOut` runs so this tab can refresh its
 * session (the cookie is already gone server-side, so the refresh resolves to
 * signed-out) and leave any authenticated-only surface. Ignores unrelated
 * `storage` writes and key removals.
 */
export function onCrossTabSignOut(onSignedOut: () => void): () => void {
  const handler = (e: StorageEvent): void => {
    // A removal (newValue === null) or an unrelated key must not trigger a
    // sign-out — only a real broadcast write does.
    if (e.key === SIGNOUT_BROADCAST_KEY && e.newValue) onSignedOut();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
